import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Controller, useForm, type Control, type UseFormRegister } from 'react-hook-form';
import { CalendarRange, RefreshCw, Save } from 'lucide-react';
import {
  Button,
  Checkbox,
  DateField,
  ErrorState,
  FormActions,
  FormField,
  FormGrid,
  Loading,
  PageHeader,
  Panel,
  Select,
  StatusBadge,
  Tabs,
  TextField,
  useConfirm,
  useToast,
} from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { IssueList } from '@/features/advance-notices/components/IssueList';
import { ApiError, errorMessage } from '@/lib/api';
import { formatNumber } from '@/lib/format';
import { toWareki } from '@/lib/wareki';
import { CATCH_LABELS, previewVoyage, useSaveVoyage, useVoyage, useVoyageNewContext, type PreviewIssue, type PreviewResult, type VoyageDetail, type VoyageNewContext } from './api';
import { ExpensePanel } from './components/ExpensePanel';
import { GearFindingTable } from './components/GearFindingTable';
import { SightingTable, type SightingsFormPart } from './components/SightingTable';
import { SurveyDaysTable } from './components/SurveyDaysTable';
import { applyRowClasses, datesBetween, dayRow, formFromContext, formFromDetail, toVoyageInput, type VoyageForm } from './form';
import '@/features/advance-notices/advance-notices.css';
import './voyage-reports.css';

/** S-41 速報 登録・編集（new?noticeVesselId= ／ :id/edit） */
export function VoyageFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { yearPath } = useFiscalYear();
  const reportId = id ? Number(id) : null;
  const detail = useVoyage(reportId);
  const rowId = reportId ? (detail.data?.noticeVesselId ?? null) : Number(params.get('noticeVesselId')) || null;
  const ctx = useVoyageNewContext(rowId);
  if (reportId && detail.isPending) return <Loading block />;
  if (reportId && (detail.error || !detail.data)) return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  if (!rowId) return <ErrorState error={new Error('事前連絡の実施船を指定してください（事前連絡一覧の「速報登録」から開いてください）。')} />;
  if (ctx.isPending) return <Loading block />;
  if (!reportId && (ctx.error || !ctx.data)) return <ErrorState error={ctx.error} onRetry={() => void ctx.refetch()} />;
  if (!reportId && ctx.data?.existingId) return <Navigate to={yearPath(`voyage-reports/${ctx.data.existingId}/edit`)} replace />;
  return <VoyageFormBody key={reportId ?? 'new'} rowId={rowId} ctx={ctx.data} detail={reportId ? detail.data : undefined} />;
}

const TAB_OF: Record<string, string> = { surveyDays: 'survey', sightings: 'sightings', gearFindings: 'gears', expense: 'expense' };

function VoyageFormBody({ rowId, ctx, detail }: { rowId: number; ctx?: VoyageNewContext; detail?: VoyageDetail }) {
  const isNew = !detail;
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { year, yearPath } = useFiscalYear();
  const save = useSaveVoyage(detail?.id ?? null);
  const form = useForm<VoyageForm>({ defaultValues: detail ? formFromDetail(detail) : formFromContext(ctx!) });
  const { register, control, handleSubmit, setValue, getValues, formState, watch } = form;
  const [tab, setTab] = useState('basic');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [errors, setErrors] = useState<PreviewIssue[]>([]);
  const [busy, setBusy] = useState(false);
  const sgControl = control as unknown as Control<SightingsFormPart>;
  const sgRegister = register as unknown as UseFormRegister<SightingsFormPart>;

  const head = {
    vesselName: detail?.vesselName ?? ctx!.prefill.vesselName,
    registrationNo: detail?.registrationNo ?? ctx!.prefill.registrationNo,
    tonnage: detail?.tonnage ?? ctx!.prefill.tonnage,
    businessOperatorId: detail?.businessOperatorId ?? ctx!.prefill.businessOperatorId,
    businessOperatorName: detail?.businessOperatorName ?? ctx!.prefill.businessOperatorName,
    fiscalYear: detail?.fiscalYear ?? ctx!.prefill.fiscalYear,
  };
  const crewList = useMemo(() => [...new Set([...(ctx?.prefill.captainOptions ?? []), ...(ctx?.prefill.crewOptions ?? [])])], [ctx]);
  const periodFrom = watch('periodFrom');
  const periodTo = watch('periodTo');

  const runPreview = async (answers: Record<string, boolean> = {}) => {
    const r = await previewVoyage(toVoyageInput(getValues(), rowId, answers, detail?.id));
    setPreview(r);
    return r;
  };
  useEffect(() => {
    void runPreview().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAuto = async () => {
    try {
      const r = await runPreview();
      getValues('surveyDays').forEach((d, i) => {
        const idx = r.calc.surveyDays.findIndex((x) => x.surveyDate === d.surveyDate);
        setValue(`surveyDays.${i}.surveyClass`, (idx >= 0 ? (r.calc.autoClasses[idx] ?? '') : '') as never, { shouldDirty: true });
      });
      toast.info('調査判定を設定しました', r.calc.autoClasses[0] ? `全行を「${r.calc.autoClasses[0]}」にしました` : '区分の付いた調査水域がありません');
      await runPreview();
    } catch (e) {
      toast.error('判定できませんでした', errorMessage(e));
    }
  };

  // 調査水域を変更した行は、計算結果の行区分で調査判定を自動更新する（kintone 検証指摘）
  const changedRows = useRef(new Set<number>());
  const zoneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onZonesChange = (i: number) => {
    changedRows.current.add(i);
    if (zoneTimer.current) clearTimeout(zoneTimer.current);
    zoneTimer.current = setTimeout(() => {
      const rows = new Set(changedRows.current);
      changedRows.current.clear();
      void runPreview()
        .then((r) => {
          const next = applyRowClasses(getValues('surveyDays'), rows, r.calc.surveyDays);
          next.forEach((d, k) => {
            if (rows.has(k)) setValue(`surveyDays.${k}.surveyClass`, d.surveyClass as never, { shouldDirty: true });
          });
        })
        .catch(() => undefined);
    }, 500);
  };
  useEffect(() => () => {
    if (zoneTimer.current) clearTimeout(zoneTimer.current);
  }, []);
  const periodLocked = !!detail?.isSubsidyPaid;

  const rebuildDays = async () => {
    if (!(await confirm({ title: '調査内容を作り直しますか？', message: '調査期間の日数分の行（調査時間 8 時間）で調査内容を置き換えます。入力済みの調査水域・感想は失われます。', confirmLabel: '作り直す', tone: 'danger' })))
      return;
    setValue('surveyDays', datesBetween(getValues('periodFrom'), getValues('periodTo')).map((d) => dayRow(d)), { shouldDirty: true });
  };

  const onSubmit = handleSubmit(async () => {
    setBusy(true);
    setErrors([]);
    try {
      let answers: Record<string, boolean> = {};
      const r = await runPreview(answers);
      if (r.errors.length) {
        setErrors(r.errors);
        const first = r.errors.find((e) => e.path)?.path?.split('.')[0] ?? (r.errors[0]?.tab ?? 'basic');
        setTab(TAB_OF[first] ?? 'basic');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (r.warnings.length) {
        const ok = await confirm({ title: '警告があります。保存しますか？', message: r.warnings.map((w) => `・${w.message}`).join('\n'), confirmLabel: '保存する' });
        if (!ok) return;
      }
      for (const c of r.confirmations) {
        const ok = await confirm({ title: '確認', message: c.message, confirmLabel: 'OK', cancelLabel: 'キャンセル' });
        if (!ok) return;
        answers = { ...answers, [c.key]: true };
      }
      const res = await save.mutateAsync(toVoyageInput(getValues(), rowId, answers, detail?.id));
      toast.success(isNew ? '速報を登録しました' : '速報を更新しました');
      navigate(yearPath(`voyage-reports/${res.id}`));
    } catch (e) {
      if (e instanceof ApiError && Array.isArray(e.details)) setErrors(e.details as PreviewIssue[]);
      else toast.error('保存できませんでした', errorMessage(e));
    } finally {
      setBusy(false);
    }
  });

  const onCancel = async () => {
    if (formState.isDirty && !(await confirm({ title: '入力内容を破棄しますか？', message: '保存していない変更は失われます。', confirmLabel: '破棄して戻る', tone: 'danger' }))) return;
    navigate(detail ? yearPath(`voyage-reports/${detail.id}`) : yearPath('advance-notices'));
  };

  const c = preview?.calc;
  const seaCard = (k: 'S' | 'A' | 'B' | 'total', label: string) => {
    const value = c ? c.seaAreaDays[k] : null;
    const limit = c ? c.limits[k] : null;
    return (
      <div className={`bs-sea-day is-${k}${value != null && limit != null && limit > 0 && value > limit ? ' is-over' : ''}`}>
        <span className="bs-sea-day__label">{label}</span>
        <span className="bs-sea-day__value">{value ?? '—'}日</span>
        <span className="bs-sea-day__limit">
          {k !== 'total' && c ? `既存 ${c.priorSeaAreaDays[k]}日 ／ ` : ''}上限 {limit ?? '—'}日
        </span>
      </div>
    );
  };

  const tabs = [
    {
      key: 'basic',
      label: '基本情報',
      content: (
        <>
          <Panel title="調査船・調査期間">
            <FormGrid columns={4}>
              <FormField label="船名">
                <TextField value={head.vesselName} readOnly />
              </FormField>
              <FormField label="漁船登録番号・トン数">
                <TextField value={`${head.registrationNo}　${formatNumber(head.tonnage, 2)}トン`} readOnly />
              </FormField>
              <FormField label="調査期間（出港日）" required hint={periodLocked ? '助成金支払日が登録済みのため変更できません' : undefined}>
                <DateField {...register('periodFrom')} readOnly={periodLocked} />
              </FormField>
              <FormField label="調査期間（帰港日）" required>
                <DateField {...register('periodTo')} readOnly={periodLocked} />
              </FormField>
            </FormGrid>
          </Panel>
          <Panel title="乗船調査員">
            <datalist id="voyage-crew-list">
              {crewList.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
            <FormGrid columns={4}>
              <FormField label="船長（乗船調査員①）" required>
                <TextField list="voyage-crew-list" {...register('captainName')} />
              </FormField>
              <FormField label="乗船調査員名②">
                <TextField list="voyage-crew-list" {...register('surveyor2Name')} />
              </FormField>
              <FormField label="乗船調査員名③">
                <TextField list="voyage-crew-list" {...register('surveyor3Name')} />
              </FormField>
              <FormField label="担い手" hint="1人あたり賃金から 3,800円 控除">
                <Select
                  options={[
                    { value: 0, label: 'なし' },
                    { value: 1, label: '1人' },
                    { value: 2, label: '2人' },
                    { value: 3, label: '3人' },
                  ]}
                  {...register('bearerCount', { valueAsNumber: true })}
                />
              </FormField>
              <FormField label="船主名">
                <TextField {...register('ownerName')} />
              </FormField>
              <FormField label="報告者氏名">
                <TextField {...register('reporterName')} />
              </FormField>
              <FormField label="漁獲調査等（ABCD）" span={2}>
                <Select placeholder="選択してください" options={Object.entries(CATCH_LABELS).map(([v, l]) => ({ value: v, label: `${v}：${l}` }))} {...register('catchSurveyStatus')} />
              </FormField>
              <FormField label="インボイス対象調査" hint="帰港日が 2023/10/1 以降のみ">
                <Controller control={control} name="isInvoiceTargetSurvey" render={({ field }) => <Checkbox label="インボイス対象調査" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} />
              </FormField>
              <FormField label="備考" span={3}>
                <TextField {...register('note')} />
              </FormField>
            </FormGrid>
          </Panel>
          <Panel title="海域別の調査日数（年度累計）" description="既存＝この速報以外の調査日。年間限度調査日数（プラス日数を含む）を超えると保存できません。">
            <div className="bs-sea-days">
              {seaCard('S', 'S海域')}
              {seaCard('A', 'A海域')}
              {seaCard('B', 'B海域')}
              {seaCard('total', '合計')}
            </div>
          </Panel>
        </>
      ),
    },
    {
      key: 'survey',
      label: '調査内容',
      badge: getValues('surveyDays').length,
      content: (
        <Panel
          title="調査内容"
          description="調査水域を入力し「自動」で調査判定（S＞A＞B）を設定します。金額は「計算・チェック」で再計算されます。"
          actions={
            isNew && (
              <Button size="sm" icon={<CalendarRange size={15} />} onClick={() => void rebuildDays()}>
                調査期間で作り直す
              </Button>
            )
          }
        >
          <SurveyDaysTable control={control} register={register} calc={c ?? null} onAuto={() => void onAuto()} onZonesChange={onZonesChange} errors={formState.errors.surveyDays as never} />
        </Panel>
      ),
    },
    {
      key: 'sightings',
      label: '視認状況報告',
      badge: getValues('sightings').length,
      content: (
        <Panel title="視認状況報告" description="調査期間内の視認情報です（定時連絡で登録した情報も表示されます。速報で確認したら参照資料「速報」にチェックしてください）。">
          <SightingTable control={sgControl} register={sgRegister} defaultDate={periodFrom} refSource="voyage" errors={formState.errors.sightings} />
        </Panel>
      ),
    },
    {
      key: 'gears',
      label: '漁場調査等',
      badge: getValues('gearFindings').length,
      content: (
        <Panel title="漁場調査等（外国漁船の漁具に関する情報）">
          <GearFindingTable control={sgControl} register={sgRegister} defaultDate={periodFrom} refSource="voyage" errors={formState.errors.gearFindings} />
        </Panel>
      ),
    },
    {
      key: 'expense',
      label: '燃油代・潤滑油・現像代',
      content: (
        <ExpensePanel control={control} register={register} setValue={setValue} getValues={getValues} fiscalYear={head.fiscalYear ?? year} businessOperatorId={head.businessOperatorId} />
      ),
    },
  ];

  return (
    <form onSubmit={onSubmit} noValidate>
      <PageHeader
        eyebrow="日々の報告 ／ 速報"
        title={isNew ? '速報 新規登録' : '速報 編集'}
        meta={<StatusBadge tone="navy">{head.vesselName}</StatusBadge>}
        description={`${head.businessOperatorName}　調査期間 ${periodFrom ? toWareki(periodFrom) : '—'} ～ ${periodTo ? toWareki(periodTo) : '—'}`}
        actions={
          <Button icon={<RefreshCw size={16} />} onClick={() => void runPreview().then((r) => setErrors(r.errors)).catch((e) => toast.error('計算できませんでした', errorMessage(e)))}>
            計算・チェック
          </Button>
        }
      />
      <IssueList tone="error" title="入力内容を確認してください" items={errors} rowLabel={(i) => `${i + 1}行目`} />
      <IssueList tone="warning" title="警告（確認のうえ保存できます）" items={errors.length ? [] : (preview?.warnings ?? [])} />
      <Tabs value={tab} onChange={setTab} items={tabs} />
      <FormActions>
        <Button onClick={() => void onCancel()}>キャンセル</Button>
        <Button type="submit" variant="primary" icon={<Save size={16} />} loading={busy}>
          {isNew ? '登録する' : '保存する'}
        </Button>
      </FormActions>
    </form>
  );
}
